import { router } from "@inertiajs/react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { money, pct, pctDelta } from "@/lib/money"
import { pluralize } from "@/lib/relative-time"
import type { WorkspaceLevel } from "@/types/bids"

/** The design's margin warning: a level whose WORST product GM is under
 *  target, with the server-computed price that restores it. Apply sends an
 *  intent — the server sets the number; the client never posts money it
 *  derived. */
export function MarginWarningBanner({ level }: { level: WorkspaceLevel }) {
  const [applying, setApplying] = useState(false)
  const { summary } = level
  if (!summary.margin_warning || summary.worst_gm == null || summary.restore_price == null) {
    return null
  }

  const gap = (Number(level.target_gm_pct) - Number(summary.worst_gm)).toFixed(1)

  return (
    <div
      className="callout-signal flex flex-wrap items-center gap-3"
      data-testid={`margin-warning-${level.id}`}
    >
      <p className="flex-1">
        <strong className="text-inherit">
          {level.name} is {pctDelta(gap).replace("+", "")} under target GM.
        </strong>{" "}
        Worst product runs {pct(summary.worst_gm)} against a {pct(level.target_gm_pct)} target —
        quoting {money(summary.restore_price)}/{level.unit_label} restores it
        {summary.affected_plans > 0 &&
          `, affecting ${pluralize(summary.affected_plans, "synced plan")}`}
        .
      </p>
      <Button
        size="sm"
        variant="secondary"
        disabled={applying}
        onClick={() => {
          setApplying(true)
          router.post(level.urls.apply_restore, {}, {
            preserveScroll: true,
            onFinish: () => setApplying(false),
          })
        }}
        data-testid={`margin-apply-${level.id}`}
      >
        Apply {money(summary.restore_price)}
      </Button>
    </div>
  )
}
